import {createAsyncThunk, createAction} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {includes} from '@shared/helpers/collection'
import {isEmpty} from '@shared/helpers/lang'

import {PageSet, PageSetType} from '@web/core/constants'
import {AccountState, bankEasyPayAccountInfoSelector} from '@web/slices/account/slice'
import {cardPromotionInfoSelector, CardState} from '@web/slices/card/slice'
import {CommonState, pageSetInfoSelector} from '@web/slices/common/slice'
import {productItemDiscountInfoSelector, OrderSheetCouponState} from '@web/slices/orderSheetCoupon/slice'
import {PurchaserState, membershipInfoSelector} from '@web/slices/purchaser/slice'

import api from './apis'
import {GetCardPromotionGroupsRejectCase, GetCardPromotionGroupsRejectCaseValues} from './constants'
import {
    CardPromotionsReloadResponse,
    GetCardPromotionResponse,
    GetPointPromotionResponse,
    PointPromotionApplicablePageSetValues,
} from './interfaces'

export type RootState = {
    card: CardState
    account: AccountState
    orderSheetCoupon: OrderSheetCouponState
    common: CommonState
    purchaser: PurchaserState
}

type ThunkApi<RejectValue = unknown> = {
    state: RootState
    rejectValue: RejectValue
}

/** 카드 프로모션 그룹 선택 */
export const selectCardPromotionGroup = createAsyncThunk<
    {
        /** 선택된 카드 프로모션 그룹의 companyCode  */
        cardCompanyCode: string
        /** 간편계좌가 없는지 여부 */
        hasNoAccounts: boolean
        /** 선택된 카드 프로모션 그룹의 카드상품 NameCode */
        cardProductNameCode: Maybe<string>
        /** 나의할인가 정책인지 여부 */
        isMyDiscount?: boolean
    },
    {cardCompanyCode: string; cardProductNameCode: Maybe<string>; isMyDiscount?: boolean},
    ThunkApi
>('cardPromotion/selectCardPromotion', ({cardCompanyCode, cardProductNameCode, isMyDiscount}, {getState}) => {
    const state = getState()

    const {accounts} = bankEasyPayAccountInfoSelector(state)

    return {
        cardCompanyCode,
        hasNoAccounts: isEmpty(accounts),
        cardProductNameCode,
        isMyDiscount,
    }
})

const isPointPromotionApplicablePageSet = (
    value: Maybe<PageSetType>,
): value is PointPromotionApplicablePageSetValues => {
    return includes(value, [PageSet.MALL_CHECKOUT, PageSet.EASY_BOOK_CHECKOUT, PageSet.SELLER_CHECKOUT])
}

const getIsMallResponse = (
    props: GetCardPromotionResponse | GetPointPromotionResponse,
): props is GetPointPromotionResponse => {
    return 'apiResultType' in props
}

/** 카드 프로모션 그룹 조회 */
export const getCardPromotionGroups = createAsyncThunk<
    CardPromotionsReloadResponse,
    void,
    ThunkApi<GetCardPromotionGroupsRejectCaseValues>
>('cardPromotion/getCardPromotions', async (_, {getState, rejectWithValue}) => {
    try {
        const state = getState()

        const {pageSet} = pageSetInfoSelector(state)
        const {productCouponDiscountAmountByItemId} = productItemDiscountInfoSelector(state)
        const {subscribed} = membershipInfoSelector(state)
        const {reloadablePromotionGroups} = cardPromotionInfoSelector(state)

        if (!isPointPromotionApplicablePageSet(pageSet)) {
            return rejectWithValue(GetCardPromotionGroupsRejectCase.CARD_PROMOTION_UNAPPLICABLE_PAGE_SET)
        }

        const cardCompanyCodes = reloadablePromotionGroups.map((group) => group.card.companyCode)

        const result = await api.getCardPromotions({
            orderSheetId: state.common.seqID,
            cardPromotionRequest: {
                selectedDiscountAmountsByOrderSheetItemId: productCouponDiscountAmountByItemId,
                membershipSubscribed: subscribed,
                cardCompanyCodes,
            },
            pageSet,
        })

        const isMallResponse = getIsMallResponse(result)

        return {
            groupedCardPromotions: isMallResponse
                ? result.groupedCardPromotions.map((cardPromotion) => {
                      return {
                          ...cardPromotion,
                          cardPromotions: undefined,
                          policies: cardPromotion.policies,
                      }
                  })
                : result.groupedCardPromotions.map((cardPromotion) => {
                      return {
                          ...cardPromotion,
                          cardPromotions: cardPromotion.cardPromotions,
                          policies: undefined,
                      }
                  }),
            apiResultType: isMallResponse ? result.apiResultType : undefined,
            failedCardPromotions: isMallResponse ? undefined : result.failedCardPromotions,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/** 선택된 카드 프로모션 취소 */
export const clearSelectedCardPromotion = createAction('card/clearSelectedCardPromotion')

export const checkCardPromotionPolicy = createAction('card/checkCardPromotionPolicy')
